import { HugeiconsIcon } from "@hugeicons/react";
import { Tick02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Checkbox as CheckboxPrimitive } from "radix-ui";
import { useId } from "react";
import { cn } from "../utils/index";
import { Field, FieldLabel } from "./field";

type CheckboxProps = React.ComponentProps<typeof CheckboxPrimitive.Root> & {
  /**
   * Renders the box with its label beside it, as the Figma checkbox field
   * (1944:482) draws it, and ties the two together with `htmlFor`.
   */
  label?: React.ReactNode;
};

// Figma (1939:2183), not upstream: --border edge, --background fill, no dark-only colours.
function Checkbox({ className, label, id, ...props }: CheckboxProps) {
  const generatedId = useId();
  const boxId = label ? (id ?? generatedId) : id;

  const box = (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      id={boxId}
      className={cn(
        "peer relative flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-border bg-background transition-colors outline-none group-has-disabled/field:opacity-50 after:absolute after:-inset-x-3 after:-inset-y-2 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 aria-invalid:aria-checked:border-primary data-checked:border-primary data-checked:bg-primary data-checked:text-primary-foreground",
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="grid place-content-center text-current transition-none [&>svg]:size-3.5"
      >
        <HugeiconsIcon icon={Tick02Icon} />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );

  if (!label) {
    return box;
  }

  return (
    <Field orientation="horizontal" data-disabled={props.disabled || undefined}>
      {box}
      <FieldLabel htmlFor={boxId}>{label}</FieldLabel>
    </Field>
  );
}

export { Checkbox };
